<template>
  <div class="phonenumber" :data-type="context.type">
    <VDropdown
      :delay="0"
      instantMove
      class="countryCode"
      @apply-show="focusSearchInput"
    >
      <div class="countryCode__code" role="button">
        <span
          >+{{ getCountryCallingCode(selectedCountry || 'NL')
          }}<i data-i="expand_more"
        /></span>
      </div>
      <template #popper="{ hide }">
        <div class="selectCountry">
          <FormKit
            :id="searchInputId"
            type="autocomplete"
            :options="countryOptions"
            :placeholder="selectedCountryLabel"
            :sectionsSchema="countryListSections"
            ignore
            openOnFocus
            @input="handleSelectedCountry(hide, $event)"
          />
        </div>
      </template>
    </VDropdown>

    <input
      v-bind="context.attrs"
      :id="context.id"
      ref="phoneInputRef"
      v-model="inputValue"
      type="tel"
      autocomplete="tel-national"
      :class="context.classes.input"
      :placeholder="placeholder"
      :disabled="context.disabled"
      :name="context.node.name"
      @blur="handleBlur"
    />
  </div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue';
import { ref, watch, computed, useId } from 'vue';
import type { FormKitFrameworkContext } from '@formkit/core';
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  getExampleNumber,
  AsYouType,
} from 'libphonenumber-js/min';
import type { CountryCode, Examples } from 'libphonenumber-js/min';
import * as countries from 'i18n-iso-countries';
import i18next from '@/helpers/i18n';

const props = defineProps({
  context: {
    type: Object as PropType<FormKitFrameworkContext & { disabled: boolean }>,
    required: true,
  },
});

const searchInputId = useId();
const phoneInputRef = ref<HTMLInputElement | null>(null);

const selectedCountry = ref<CountryCode>('NL');
const inputValue = ref('');

// Update the formatted input value as the user types
// Model updates are handled in the blur event
watch(
  () => inputValue.value,
  (value) => {
    const newVal = new AsYouType(selectedCountry.value)
      .input(value)
      // Ensure that partial numbers with trailing non-digit characters are editable.
      .replace(/\D+$/, '');

    if (newVal === value) {
      return;
    }
    inputValue.value = newVal;
  }
);

watch(
  () => props.context._value,
  (newValue) => {
    updateValues(newValue || '', selectedCountry.value);
  },
  { immediate: true }
);

const countryOptions = computed(() =>
  getCountries()
    .reduce(
      (options, countryCode) => {
        const callingCode = getCountryCallingCode(countryCode);
        const countryName = countries.getName(
          countryCode,
          i18next.resolvedLanguage as string,
          {
            select: 'official',
          }
        );
        if (callingCode && countryName) {
          options.push({
            value: countryCode,
            label: `${countryName} +${callingCode}`,
          });
        }
        return options;
      },
      [] as { value: string; label: string }[]
    )
    .sort((a, b) => a.label.localeCompare(b.label))
);

// The dropdown is the list, so it stays in the flow instead of floating under the search.
const countryListSections = {
  dropdownWrapper: {
    attrs: { style: { maxHeight: '20rem', overflow: 'auto' } },
  },
};

const selectedCountryLabel = computed(
  () =>
    countryOptions.value.find(({ value }) => value === selectedCountry.value)
      ?.label
);

// Example numbers for EU countries plus US and Australia
// Libphonenumber-js provides examples but only for every country.
// Defining our own set here to limit to relevant countries.
const EXAMPLES = {
  AT: '664123456', // Austria
  BE: '470123456', // Belgium
  BG: '43012345', // Bulgaria
  HR: '921234567', // Croatia
  CY: '96123456', // Cyprus
  CZ: '601123456', // Czech Republic
  DK: '34412345', // Denmark
  EE: '51234567', // Estonia
  FI: '412345678', // Finland
  FR: '612345678', // France
  DE: '15123456789', // Germany
  GR: '6912345678', // Greece
  HU: '201234567', // Hungary
  IE: '850123456', // Ireland
  IT: '3123456789', // Italy
  LV: '21234567', // Latvia
  LT: '61234567', // Lithuania
  LU: '628123456', // Luxembourg
  MT: '96961234', // Malta
  NL: '612345678', // Netherlands
  PL: '512345678', // Poland
  PT: '912345678', // Portugal
  RO: '712034567', // Romania
  SK: '912123456', // Slovakia
  SI: '31234567', // Slovenia
  ES: '612345678', // Spain
  SE: '701234567', // Sweden
  GB: '7400123456', // United Kingdom
  NO: '40612345', // Norway (Scandinavia, not EU)
  IS: '6111234', // Iceland (Scandinavia, not EU)
  AU: '412345678', // Australia
  US: '2015550123', // United States
} as Examples;

const placeholder = computed(() => {
  if (selectedCountry.value) {
    const exampleNumber = getExampleNumber(selectedCountry.value, EXAMPLES);

    if (exampleNumber) {
      return exampleNumber.format('NATIONAL', {
        nationalPrefix: false,
        humanReadable: true,
      });
    }
  }
  return '';
});

function handleBlur(event: FocusEvent) {
  updateValues(inputValue.value, selectedCountry.value);
  props.context.handlers?.blur?.(event);
}

function handleSelectedCountry(hide: () => void, country: unknown) {
  if (!country) return;
  selectedCountry.value = country as CountryCode;
  phoneInputRef.value?.focus();
  hide();
}

function updateValues(value: string, countryCode: CountryCode) {
  const parsed = parsePhoneNumberFromString(value, countryCode);

  const newCountry = parsed?.country || countryCode || 'NL';
  const newNumber = parsed?.number || value || '';
  const newInputValue =
    parsed?.format('NATIONAL', {
      nationalPrefix: false,
      humanReadable: true,
    }) ||
    value ||
    '';

  if (newCountry !== selectedCountry.value) {
    selectedCountry.value = newCountry;
  }

  if (newInputValue !== inputValue.value) {
    inputValue.value = newInputValue;
  }

  if (newNumber !== props.context._value) {
    props.context.node.input(newNumber);
  }
}

function focusSearchInput() {
  // Delay to ensure the dropdown transition has completed.
  // Using nextTick did not work reliably here.
  setTimeout(() => {
    document.getElementById(searchInputId)?.focus();
  }, 100);
}
</script>

<style scoped>
.selectCountry {
  width: 18rem;

  &:deep(.formkit-outer) {
    margin: 0;
  }

  &:deep(.formkit-inner) {
    flex-wrap: wrap;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }

  &:deep(.formkit-select-icon) {
    display: none;
  }

  &:deep(.formkit-dropdown-wrapper) {
    flex-basis: 100%;
    border-top: 1px solid var(--neutral-400);
    box-shadow: none;
  }
}

.phonenumber {
  flex-basis: 100%;
  position: relative;

  input {
    padding-left: 4.5em;
  }
}

.countryCode {
  white-space: nowrap;
  background-color: var(--neutral-200);
  border-right: 1px solid var(--neutral-400);
  height: 100%;
  position: absolute;
  text-align: center;
  width: 4em;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-top-left-radius: var(--fk-border-radius-tl);
  border-bottom-left-radius: var(--fk-border-radius-bl);
  color: var(--neutral-600);
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    cursor: pointer;
    background-color: var(--neutral-300);
  }

  .countryCode__code {
    line-height: 100%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    [data-i]::before {
      font-size: 1rem;
      width: 0.8em;
      margin: 0;
    }
  }
}
</style>
